import { useTranslations } from '@vocab/react';
import {
  ButtonLink,
  IconNewWindow,
  Text,
  TooltipRenderer,
} from 'braid-design-system';

import { EVENT_NAME_PAY_PER_PLACEMENT_BUTTON_PRESSED } from 'src/App/constants/tracking';
import {
  PayPerPlacementProduct,
  type JobListItemFragment,
  type PayPerPlacementEligibleResponse,
} from 'src/graphql/__generated__';
import { useTracking } from 'src/hooks/useTracking';

import translations from './.vocab';

interface PayPerPlacementButtonProps extends PayPerPlacementEligibleResponse {
  jobId: JobListItemFragment['id'];
}

export const PayPerPlacementButton: React.FC<
  React.PropsWithChildren<PayPerPlacementButtonProps>
> = ({ jobId, redirectUrl, product }) => {
  const { t } = useTranslations(translations);
  const { trackEvent } = useTracking();

  const onClick = () => {
    trackEvent(EVENT_NAME_PAY_PER_PLACEMENT_BUTTON_PRESSED, {
      jobId,
      productOffered:
        product === PayPerPlacementProduct.RECRUITER_NETWORK
          ? 'recruiter network'
          : 'smart hire',
    });
  };

  const text = t(
    product === PayPerPlacementProduct.RECRUITER_NETWORK
      ? 'Try a recruiter'
      : 'Try SmartHire',
  );
  const tooltip = t(
    product === PayPerPlacementProduct.RECRUITER_NETWORK
      ? 'Connect to a trusted recruiter for faster hiring'
      : 'Hire faster with quality, vetted candidates',
  );
  return (
    <TooltipRenderer
      id="pay-per-placement-tooltip"
      tooltip={<Text>{tooltip}</Text>}
    >
      {({ triggerProps }) => (
        <ButtonLink
          href={redirectUrl}
          variant="ghost"
          size="small"
          tone="brandAccent"
          icon={<IconNewWindow />}
          iconPosition="trailing"
          target="_blank"
          {...triggerProps}
          onClick={onClick}
        >
          {text}
        </ButtonLink>
      )}
    </TooltipRenderer>
  );
};
